import styled, { css, keyframes } from 'styled-components';

import { BRAND_SWITCHER, BRAND_SWITCHER_ANIM as A, MOBILE } from '../../theme/tokens';
import { hoverWash } from '../../theme/mixins';
import { Text } from '../Text/Text';

const NO_MOTION = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Shared staggered reveal: fade (+ optional slide) driven by `$entered`, with a
// per-element `$delayMs` that only applies on open so the panel still closes
// cleanly as one piece. Used by the brand rows, divider and "New brand" button.
const reveal = (p: { $entered?: boolean; $delayMs?: number }, slide = '6px') => css`
  opacity: ${p.$entered ? 1 : 0};
  transform: ${p.$entered ? 'translateY(0)' : `translateY(${slide})`};
  transition:
    opacity ${p.$entered ? A.FADE_OPEN_MS : A.FADE_MS}ms ${A.EASE},
    transform ${p.$entered ? A.FADE_OPEN_MS : A.FADE_MS}ms ${A.EASE};
  transition-delay: ${p.$entered ? p.$delayMs ?? 0 : 0}ms;
  ${NO_MOTION}
`;

export const Root = styled.div<{ $styleText: string }>`
  position: relative;
  display: inline-block;
  font: inherit;
  ${(p) => p.$styleText}
`;

// `$frame` is picked per-logo from the artwork's luminance (`useLogoFrame`): a
// light/white mark gets the dark tile, a dark/black mark the light tile, so the
// logo always reads — both in the dropdown rows and the collapsed top-bar trigger
// (which used to sit on a bare transparent tile and could hide a dark mark against
// the dark chrome). The background eases when the frame resolves after measuring,
// so the correction settles rather than popping.
export const Logo = styled.span<{ $frame: 'light' | 'dark' }>`
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: ${(p) => (p.$frame === 'light' ? BRAND_SWITCHER.logoBgLight : BRAND_SWITCHER.logoBgDark)};
  color: ${BRAND_SWITCHER.nameActive};
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  transition: background-color ${A.PANEL_OPEN_MS}ms ${A.EASE};
  ${NO_MOTION}

  img {
    width: 100%;
    height: 100%;
    padding: 8px;
    box-sizing: border-box;
    object-fit: contain;
    display: block;
  }
`;

const placeholderPulse = keyframes`
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.85;
  }
`;

// Neutral stand-in shown in the collapsed trigger while the brand list is still
// loading (or, in the rare authenticated-but-brandless edge, empty). This
// replaces the old `name.charAt(0) || '?'` fallback that surfaced a literal "?"
// whenever there was no active brand yet — the visible symptom of a stale/empty
// session. A muted rounded tile with a gentle pulse, sized to the logo box.
export const LogoPlaceholder = styled.span`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: ${BRAND_SWITCHER.logoBg};
  animation: ${placeholderPulse} ${A.PLACEHOLDER_PULSE_MS}ms ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Collapsed trigger: brand logo + a vertical double-chevron, no name, no background.
export const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
`;

// Expanded: overlays the trigger so the active brand's logo stays aligned. The
// panel fades opacity only (no transform) — that's the "background fades in"
// around the icon, which the collapsed trigger keeps visible underneath. The name
// and remaining rows layer their own delayed reveals on top of this fade.
//
// Rendered in a portal on `document.body` and positioned `fixed` from the
// trigger's viewport rect (`$top`/`$left` mirror the old `-8px` anchor offset):
// the layout chrome (`.pg-body` / `.pg-layout`) is `overflow: hidden`, which
// clipped the old `absolute` panel. Escaping to the body lets it open at full
// width and show its glow.
export const Menu = styled.div<{ $entered: boolean; $top: number; $left: number }>`
  position: fixed;
  top: ${(p) => p.$top}px;
  left: ${(p) => p.$left}px;
  // Base 200px + the two trailing action buttons on the active row (open-site +
  // settings, 28px each) and their 8px gaps: widening the panel by their footprint
  // hands that width back to the brand name so a long name reads in full instead of
  // truncating to an ellipsis.
  width: calc(200px + 2 * (28px + 8px));
  // Never extend past the viewport: cap to the space below the panel's top
  // (anchored at $top), leaving a 16px breathing gap at the bottom. When the
  // brand list outgrows this, BrandList scrolls while the divider + "New brand"
  // button stay pinned at the bottom.
  max-height: calc(100vh - ${(p) => p.$top}px - 16px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 8px 12px;
  background: ${BRAND_SWITCHER.panel};
  border-radius: 12px;
  box-shadow: 0 0 128px ${BRAND_SWITCHER.shadow};
  overflow: hidden;
  z-index: 1000;
  transform-origin: top left;
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  pointer-events: ${(p) => (p.$entered ? 'auto' : 'none')};
  transition: opacity ${(p) => (p.$entered ? A.PANEL_OPEN_MS : A.PANEL_MS)}ms ${A.EASE};
  ${NO_MOTION}
`;

// Edge-aware scroll fade. The gradient stops are mask ALPHA, not design colours
// (#000 = keep, transparent = hide), so they aren't tokenised — same rationale as
// `fadeMaskY` in theme/mixins.ts. Each edge only ramps when there's hidden
// content past it, so the active first row stays fully opaque at rest and the
// last row dissolves (rather than hard-cuts) into the divider while scrolling.
const SCROLL_FADE_PX = 24;
const scrollMask = (fadeTop: boolean, fadeBottom: boolean): string => {
  if (!fadeTop && !fadeBottom) {
    return 'none';
  }
  const start = fadeTop ? `transparent 0, #000 ${SCROLL_FADE_PX}px` : '#000 0';
  const end = fadeBottom ? `#000 calc(100% - ${SCROLL_FADE_PX}px), transparent 100%` : '#000 100%';
  return `linear-gradient(to bottom, ${start}, ${end})`;
};

// The scrollable region: takes the slack height the Menu allows (`flex: 1` +
// `min-height: 0` so it can shrink below content size) and scrolls its rows
// when there are more brands than fit. A thin overlay scrollbar keeps the
// dark panel clean.
export const BrandList = styled.div<{ $scrollable: boolean; $fadeTop: boolean; $fadeBottom: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Only scroll when the list genuinely overflows. While it fits, stay fully
  // hidden so the open cascade's transient row transforms (translateY) cannot
  // flash a scrollbar. The two-value form clips the x-axis so a forced y
  // scrollbar never implies an x one.
  overflow: ${(p) => (p.$scrollable ? 'hidden auto' : 'hidden')};
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: ${BRAND_SWITCHER.border} transparent;
  -webkit-mask-image: ${(p) => scrollMask(p.$fadeTop, p.$fadeBottom)};
  mask-image: ${(p) => scrollMask(p.$fadeTop, p.$fadeBottom)};

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${BRAND_SWITCHER.border};
    border-radius: 9999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// `$lead` is the active brand (first row): it stays put so its icon reads as
// "already there" and its name/check reveal individually. Other rows cascade.
// Now a plain layout row (not the clickable element): the select target and the
// settings cog are separate sibling buttons, so neither nests inside the other
// (a button inside a button is invalid and breaks focus/click handling).
export const Row = styled.div<{ $entered: boolean; $delayMs: number; $lead: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  ${(p) => (p.$lead ? '' : reveal(p))}
`;

// The brand-select target: fills the row so clicking the logo/name (or the slack
// beside it) activates the brand, leaving only the trailing actions un-clickable.
export const RowSelect = styled.button`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font: inherit;
`;

export const RowLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
`;

// Trailing actions parked at the row's right edge: the active check and, on the
// active row only, the settings cog.
export const RowActions = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

// `$animate` (lead row only) fades the name in after the background; non-lead
// names are static and ride their row's slide-in instead.
export const Name = styled(Text)<{ $active: boolean; $animate?: boolean; $entered?: boolean; $delayMs?: number }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  font-weight: ${(p) => (p.$active ? 700 : 300)};
  color: ${(p) => (p.$active ? BRAND_SWITCHER.nameActive : BRAND_SWITCHER.nameInactive)};
  ${(p) =>
    p.$animate
      ? css`
          opacity: ${p.$entered ? 1 : 0};
          transform: ${p.$entered ? 'translateX(0)' : 'translateX(-4px)'};
          transition:
            opacity ${p.$entered ? A.FADE_OPEN_MS : A.FADE_MS}ms ${A.EASE},
            transform ${p.$entered ? A.FADE_OPEN_MS : A.FADE_MS}ms ${A.EASE};
          transition-delay: ${p.$entered ? p.$delayMs ?? 0 : 0}ms;
          ${NO_MOTION}
        `
      : ''}
`;

// Active row's check fades in alongside the name (via `$delayMs`); inactive rows
// stay at opacity 0 regardless.
export const CheckSlot = styled.span<{ $active: boolean; $entered?: boolean; $delayMs?: number }>`
  flex: 0 0 16px;
  display: inline-flex;
  opacity: ${(p) => (p.$active && p.$entered ? 1 : 0)};
  transition: opacity ${(p) => (p.$entered ? A.FADE_OPEN_MS : A.FADE_MS)}ms ${A.EASE};
  transition-delay: ${(p) => (p.$entered ? p.$delayMs ?? 0 : 0)}ms;
  ${NO_MOTION}
`;

// Icon action buttons on the active row, sitting just past the check: "open the
// live site" then the settings cog. Each glyph rides the button's `currentColor`
// (no explicit Icon colour) so the hover wash can lift it from the muted chevron
// tone to full white. They fade in alongside the check via `$delayMs` (opacity
// only); the hover colour stays instant.
//
// The hover/press wash lives on a fixed 28px centred `::before`, NOT on the
// button box. That decouples the VISIBLE chip from the HIT AREA: the button box
// can grow to a 48px touch target on phones while the wash stays 28px and
// centred, so it never reaches (and gets clipped by) the panel's `overflow:
// hidden` edge. The glyph is lifted above the wash with `position: relative`.
export const RowActionButton = styled.button<{ $entered?: boolean; $delayMs?: number }>`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${BRAND_SWITCHER.chevron};
  cursor: pointer;
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition:
    opacity ${(p) => (p.$entered ? A.FADE_OPEN_MS : A.FADE_MS)}ms ${A.EASE},
    color ${A.FADE_OPEN_MS}ms ${A.EASE};
  transition-delay: ${(p) => (p.$entered ? p.$delayMs ?? 0 : 0)}ms, 0ms;
  ${NO_MOTION}

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 28px;
    height: 28px;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    background: transparent;
    transition: background-color ${A.FADE_OPEN_MS}ms ${A.EASE};
  }

  & > svg {
    position: relative;
    z-index: 1;
  }

  &:hover {
    color: ${BRAND_SWITCHER.nameActive};
  }

  &:hover::before,
  &:active::before {
    background: ${BRAND_SWITCHER.border};
  }

  // Meet the 48px minimum touch target on phones without widening the row: the
  // box grows to 48px while a -10px margin (=(48-28)/2) pulls its layout
  // footprint back to the 28px desktop size, so the hit area expands around the
  // glyph but the active brand's name keeps its width. 48px tall also fills the
  // row exactly (it's logo-height), so a tap anywhere on the right reaches it.
  // The wash (::before) stays 28px and centred, so it never clips at the edge.
  ${MOBILE} {
    width: 48px;
    height: 48px;
    margin: -10px;
  }
`;

export const Divider = styled.div<{ $entered?: boolean; $delayMs?: number }>`
  /* Never shrinks: with many brands only the list gives up height (and scrolls). */
  flex: 0 0 auto;
  width: 100%;
  height: 1px;
  background: ${BRAND_SWITCHER.border};
  opacity: ${(p) => (p.$entered ? 1 : 0)};
  transition: opacity ${(p) => (p.$entered ? A.FADE_OPEN_MS : A.FADE_MS)}ms ${A.EASE};
  transition-delay: ${(p) => (p.$entered ? p.$delayMs ?? 0 : 0)}ms;
  ${NO_MOTION}
`;

export const NewBrandButton = styled.button<{ $entered?: boolean; $delayMs?: number }>`
  /* Never shrinks: a long brand list squeezed it below its 48px (the list scrolls instead). */
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  // Snug horizontal padding so the longer "Continue setup" label fits on one line in
  // the 200px menu (the wider "New brand" copy stays comfortably centered). Longer
  // translations (e.g. es "Continuar configuración") still wrap gracefully to two
  // 24px lines, which the fixed 48px height accommodates exactly.
  padding: 0 12px;
  border: 0;
  border-radius: 9999px;
  background: ${BRAND_SWITCHER.accent};
  color: ${BRAND_SWITCHER.onAccent};
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring, shared with the CTA Button. */
  ${hoverWash}
  ${(p) => reveal(p)}
`;

export const NewBrandLabel = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${BRAND_SWITCHER.onAccent};
`;
